import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import { logError } from '@/utilities/logger';
import { isInWorkbookRouteAndWorkbookLoaded } from '@/utilities/utilities';
import { getCommonAttributes, getMeter, serializeError } from '@seeqdev/telemetry';

type Meter = NonNullable<ReturnType<typeof getMeter>>;
type Counter = ReturnType<Meter['createCounter']>;
type Histogram = ReturnType<Meter['createHistogram']>;
type Attributes = ReturnType<typeof getCommonAttributes>;

/**
 * Telemetry for reads of persisted UI state (worksteps, interactive content
 * blobs, tool configs, workbench state, Action Board tiles). The goal is to make
 * the "new code reads old state" failure class observable: every read records
 * how far behind the current schema the blob was, and every failure along the
 * read path (upgrade, rehydrate, transform, render) is attributed with that same
 * age, so a crash caused by stale state is distinguishable from any other
 * frontend error and the failing slice (store) is identifiable.
 *
 * All metric recording is a no-op until the telemetry SDK is initialized (gated
 * by Features/Telemetry/Webserver/Enabled). The error log line goes through the
 * regular logger so it also reaches Loki via the legacy /client-log transport on
 * servers where the SDK is off.
 */

/** Which kind of persisted blob was read. */
export type StateKind = 'workstep' | 'content' | 'workbench' | 'workbook' | 'toolConfig' | 'actionBoard';

/** Where along the read path the failure happened. */
export type StatePhase = 'upgrade' | 'rehydrate' | 'transform' | 'render';

/** The schema-version range a blob was read across. */
export interface StateOrigin {
  fromVersion: number;
  toVersion: number;
}

export interface StateErrorRecord {
  kind: StateKind;
  phase: StatePhase;
  error: unknown;
  /** The blob's schema-version range; both are omitted when the failure cannot be tied to a specific read. */
  fromVersion?: number;
  toVersion?: number;
  /** The `upgradeN` step that threw (upgrade phase only). */
  upgraderStep?: number;
  /** The store whose rehydrate threw (rehydrate phase only). */
  store?: string;
}

// Lazily-created instruments; null until a Meter is available.
let readCounter: Counter | null = null;
let readSizeHistogram: Histogram | null = null;
let upgradeDurationHistogram: Histogram | null = null;
let errorCounter: Counter | null = null;

// The origin of the blob whose state is currently live in the stores, per kind.
// Rehydrate and worksheet-level render failures happen after the upgrader chain
// has returned and have no version in scope, so they attribute themselves to this.
// It is set when state is rehydrated, not when a blob is read: worksteps are also
// read in the background (copying a display range to other worksheets, report
// content, Vantage column configuration) and those reads never reach the stores.
let loaded: Partial<Record<StateKind, StateOrigin>> = {};

function getReadCounter(): Counter | null {
  if (!readCounter) {
    readCounter =
      getMeter()?.createCounter('seeq.state.read.count', {
        description: 'Count of persisted UI state blobs read (rehydrated), by schema-version lag',
        unit: '1',
      }) ?? null;
  }
  return readCounter;
}

function getReadSizeHistogram(): Histogram | null {
  if (!readSizeHistogram) {
    readSizeHistogram =
      getMeter()?.createHistogram('seeq.state.read.size', {
        description: 'Serialized size of persisted UI state blobs as read from the API',
        unit: 'By',
        advice: {
          explicitBucketBoundaries: [
            1_000, 10_000, 50_000, 100_000, 250_000, 500_000, 1_000_000, 2_000_000, 5_000_000, 10_000_000,
          ],
        },
      }) ?? null;
  }
  return readSizeHistogram;
}

function getUpgradeDurationHistogram(): Histogram | null {
  if (!upgradeDurationHistogram) {
    upgradeDurationHistogram =
      getMeter()?.createHistogram('seeq.state.upgrade.duration', {
        description: 'Time spent running the schema upgrader chain over a persisted UI state blob',
        unit: 'ms',
        advice: { explicitBucketBoundaries: [0.5, 1, 2, 5, 10, 25, 50, 100, 250, 500, 1_000, 2_500] },
      }) ?? null;
  }
  return upgradeDurationHistogram;
}

function getErrorCounter(): Counter | null {
  if (!errorCounter) {
    errorCounter =
      getMeter()?.createCounter('seeq.state.error.count', {
        description:
          'Count of failures reading persisted UI state (upgrade, rehydrate, transform, render), by schema-version lag',
        unit: '1',
      }) ?? null;
  }
  return errorCounter;
}

/**
 * Attributes common to all state-read telemetry: the telemetry common
 * attributes (route) plus whether this is a synthetic session (renderer's
 * headless captures re-read content on schedules and would otherwise dominate
 * the counters), so real-user impact is separable in dashboards.
 */
function stateAttributes(kind: StateKind): Attributes {
  return { ...getCommonAttributes(), 'session.synthetic': headlessRenderMode(), 'state.kind': kind };
}

/**
 * Buckets a schema-version lag into a small, fixed set of labels so the metric
 * attribute stays low-cardinality. "current" means the blob was written by this
 * schema version; anything else measures how far behind it was.
 */
export function versionLagBucket(lag: number): string {
  if (lag <= 0) return 'current';
  if (lag <= 5) return '1-5';
  if (lag <= 20) return '6-20';
  if (lag <= 50) return '21-50';
  return '50+';
}

/**
 * Version attributes for a blob: the bucketed lag (joins with the read counter)
 * and, when known, the exact origin version (errors are rare, so exact values
 * are affordable and make a bad upgrader step identifiable).
 */
function originAttributes({ fromVersion, toVersion }: Partial<StateOrigin>): Attributes {
  if (fromVersion === undefined || toVersion === undefined) {
    return { 'state.version_lag': 'unknown' };
  }
  return { 'state.from_version': fromVersion, 'state.version_lag': versionLagBucket(toVersion - fromVersion) };
}

/** The origin of the blob whose state is live in the stores of the given kind, if one is. */
export function loadedStateOrigin(kind: StateKind): StateOrigin | undefined {
  return loaded[kind];
}

/**
 * Records that a persisted blob was read and how far behind the current schema
 * it was. Call at the read boundary, before upgraders run.
 */
export function recordStateRead(kind: StateKind, fromVersion: number, toVersion: number): void {
  getReadCounter()?.add(1, { ...stateAttributes(kind), ...originAttributes({ fromVersion, toVersion }) });
}

/**
 * Records that the stores of the given kind now hold state from the blob with
 * this origin, so failures while that state is live (a store's rehydrate, a
 * worksheet render) can be attributed to it. Call with no origin when the
 * stores were reset or filled with state that was not read from a blob.
 */
export function recordStateLoaded(kind: StateKind, origin: StateOrigin | undefined): void {
  loaded = { ...loaded, [kind]: origin };
}

/**
 * Records the serialized size of a blob as it came off the wire, so the
 * distribution of blob sizes (and therefore the cost of any per-read parsing or
 * validation) is known rather than guessed.
 */
export function recordStateSize(kind: StateKind, sizeBytes: number): void {
  getReadSizeHistogram()?.record(sizeBytes, stateAttributes(kind));
}

/**
 * Records how long the upgrader chain took for a blob. Only successful chains
 * are recorded; failures are counted by {@link recordStateError}.
 */
export function recordUpgradeDuration(kind: StateKind, origin: StateOrigin, durationMs: number): void {
  getUpgradeDurationHistogram()?.record(durationMs, { ...stateAttributes(kind), ...originAttributes(origin) });
}

/**
 * Records a failure along the read path of persisted state, attributed with the
 * blob's origin version, the phase that failed, and (where applicable) the
 * upgrader step or store that threw. Also logs a line carrying the same
 * attributes so the failure is diagnosable and grep-able in Loki. This is
 * observability only and must not change behavior: callers rethrow, or swallow
 * the error exactly as they did before.
 */
export function recordStateError({ kind, phase, error, upgraderStep, store, ...origin }: StateErrorRecord): void {
  const attributes: Attributes = {
    ...stateAttributes(kind),
    ...originAttributes(origin),
    'state.phase': phase,
    'error.type': error instanceof Error ? error.constructor.name : typeof error,
    ...(upgraderStep !== undefined && { 'state.upgrader_step': upgraderStep }),
    ...(store !== undefined && { 'state.store': store }),
  };
  getErrorCounter()?.add(1, attributes);

  const where = store ? ` in ${store}` : upgraderStep !== undefined ? ` at upgrade${upgraderStep}` : '';
  const version =
    origin.fromVersion === undefined || origin.toVersion === undefined
      ? 'unknown version'
      : `v${origin.fromVersion} (current v${origin.toVersion})`;
  logError(
    `Stale-state ${phase} failure: ${kind} state from ${version}${where}: ${serializeError(error)}`,
    'stateTelemetry',
    attributes,
  );
}

/**
 * Records a failure that happened after the upgrader chain returned (a store's
 * rehydrate, or a worksheet-level render), attributing it to the blob whose
 * state is loaded for that kind. When nothing of that kind is loaded, the
 * version is recorded as unknown.
 */
export function recordLoadedStateError(kind: StateKind, phase: StatePhase, error: unknown, store?: string): void {
  recordStateError({ kind, phase, error, store, ...loaded[kind] });
}

/**
 * Records a React render failure as a workstep read failure, attributed to the
 * loaded workstep. Only failures inside a loaded workbook count: the loaded
 * origin outlives navigation, so a crash on the home screen or an admin page after
 * a worksheet was open would otherwise be blamed on that worksheet's saved state.
 * Outside a workbook nothing on screen came from a workstep, so the error is
 * left to the error boundary's ordinary logging.
 */
export function recordWorksheetRenderError(error: unknown): void {
  if (!isInWorkbookRouteAndWorkbookLoaded()) {
    return;
  }
  recordLoadedStateError('workstep', 'render', error);
}

/** Visible for testing only. Clears cached instruments and the loaded origins so tests can re-init the SDK. */
export function resetStateTelemetryForTesting(): void {
  readCounter = null;
  readSizeHistogram = null;
  upgradeDurationHistogram = null;
  errorCounter = null;
  loaded = {};
}
